import React, { useState, useEffect } from 'react';
import { Gift, Check, Play } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReferralSettingsCard = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/settings/referral`, authHeaders());
        setSettings(res.data);
      } catch (e) {
        console.error('Error loading referral settings:', e);
      }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/referral`, {
        enabled: settings.enabled,
        referrer_reward_type: settings.referrer_reward_type,
        referrer_reward_points: Number(settings.referrer_reward_points) || 10,
        referrer_discount_percent: Number(settings.referrer_discount_percent) || 10,
        friend_discount_percent: Number(settings.friend_discount_percent) || 10,
        friend_discount_valid_days: Number(settings.friend_discount_valid_days) || 14,
        promo_enabled: !!settings.promo_enabled,
        promo_every_n_bookings: Number(settings.promo_every_n_bookings) || 5,
        link_message_template: settings.link_message_template || '',
        promo_message_template: settings.promo_message_template || '',
      }, authHeaders());
      toast.success('Empfehlungsprogramm gespeichert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleRunRewards = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/settings/referral/process-rewards`, {}, authHeaders());
      toast.success(`Belohnungen verarbeitet: ${res.data.rewarded || 0} Werber belohnt`);
    } catch (e) {
      toast.error('Fehler beim Verarbeiten');
    } finally {
      setRunning(false);
    }
  };

  if (!settings) return null;
  const stats = settings.stats || {};

  return (
    <Card className="border-emerald-200" data-testid="referral-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Gift className="text-emerald-600" />
              Empfehlungsprogramm
            </CardTitle>
            <CardDescription>
              Kunden fordern per WhatsApp ihren Empfehlungslink an („Empfehlungslink"). Der Freund
              bucht über den Link und erhält einen Willkommens-Rabatt. Der Werber wird belohnt,
              sobald der Freund sein erstes Training <strong>abgeschlossen</strong> hat (täglich 09:15 geprüft).
            </CardDescription>
          </div>
          <Switch
            checked={!!settings.enabled}
            onCheckedChange={(v) => setSettings({ ...settings, enabled: !!v })}
            data-testid="referral-toggle"
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="p-2 bg-emerald-50 rounded-lg">
            <p className="text-lg font-bold text-emerald-700" data-testid="referral-stat-links">{stats.total_links ?? 0}</p>
            <p className="text-xs text-gray-600">Links erstellt</p>
          </div>
          <div className="p-2 bg-emerald-50 rounded-lg">
            <p className="text-lg font-bold text-emerald-700" data-testid="referral-stat-registered">{stats.registered ?? 0}</p>
            <p className="text-xs text-gray-600">Geworben (offen)</p>
          </div>
          <div className="p-2 bg-emerald-50 rounded-lg">
            <p className="text-lg font-bold text-emerald-700" data-testid="referral-stat-rewarded">{stats.rewarded ?? 0}</p>
            <p className="text-xs text-gray-600">Belohnt</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Belohnung für den Werber</Label>
            <Select
              value={settings.referrer_reward_type || 'points'}
              onValueChange={(v) => setSettings({ ...settings, referrer_reward_type: v })}
            >
              <SelectTrigger data-testid="referral-reward-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="points">Punkte-Gutschrift</SelectItem>
                <SelectItem value="discount">Rabattcode</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {settings.referrer_reward_type === 'discount' ? (
            <div className="space-y-2">
              <Label>Werber-Rabatt in %</Label>
              <Input
                data-testid="referral-referrer-percent"
                type="number" min="1" max="100"
                value={settings.referrer_discount_percent ?? 10}
                onChange={(e) => setSettings({ ...settings, referrer_discount_percent: e.target.value })}
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Punkte für den Werber</Label>
              <Input
                data-testid="referral-referrer-points"
                type="number" min="1"
                value={settings.referrer_reward_points ?? 10}
                onChange={(e) => setSettings({ ...settings, referrer_reward_points: e.target.value })}
              />
            </div>
          )}
          <div className="space-y-2">
            <Label>Willkommens-Rabatt Freund in %</Label>
            <Input
              data-testid="referral-friend-percent"
              type="number" min="1" max="100"
              value={settings.friend_discount_percent ?? 10}
              onChange={(e) => setSettings({ ...settings, friend_discount_percent: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>Gültigkeit Freund-Code (Tage)</Label>
            <Input
              data-testid="referral-friend-days"
              type="number" min="1"
              value={settings.friend_discount_valid_days ?? 14}
              onChange={(e) => setSettings({ ...settings, friend_discount_valid_days: e.target.value })}
            />
          </div>
        </div>

        <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">🔗 Proaktiver Empfehlungslink</p>
              <p className="text-xs text-gray-600">
                Der Bot bietet den Link automatisch nach jeder N-ten Buchung an — das Programm bewirbt sich selbst.
              </p>
            </div>
            <Switch
              checked={!!settings.promo_enabled}
              onCheckedChange={(v) => setSettings({ ...settings, promo_enabled: !!v })}
              data-testid="referral-promo-toggle"
            />
          </div>
          {settings.promo_enabled && (
            <div className="space-y-1 w-48">
              <Label className="text-xs">Nach jeder ... Buchung</Label>
              <Input
                data-testid="referral-promo-n"
                type="number" min="1"
                value={settings.promo_every_n_bookings ?? 5}
                onChange={(e) => setSettings({ ...settings, promo_every_n_bookings: e.target.value })}
              />
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label>Nachricht „Empfehlungslink anfordern"</Label>
          <Textarea
            data-testid="referral-link-template"
            rows={5}
            value={settings.link_message_template || ''}
            onChange={(e) => setSettings({ ...settings, link_message_template: e.target.value })}
          />
          <p className="text-xs text-gray-500">
            Platzhalter: {'{greeting}'}, {'{first_name}'}, {'{link}'}, {'{friend_discount_percent}'}, {'{reward}'}
          </p>
        </div>

        <div className="space-y-2">
          <Label>Nachricht „Proaktiver Link nach N. Buchung"</Label>
          <Textarea
            data-testid="referral-promo-template"
            rows={4}
            value={settings.promo_message_template || ''}
            onChange={(e) => setSettings({ ...settings, promo_message_template: e.target.value })}
          />
          <p className="text-xs text-gray-500">
            Platzhalter: {'{booking_count}'}, {'{link}'}, {'{friend_discount_percent}'}, {'{reward}'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="referral-save-btn">
            <Check className="h-4 w-4 mr-2" />
            {saving ? 'Speichert...' : 'Speichern'}
          </Button>
          <Button variant="outline" onClick={handleRunRewards} disabled={running} data-testid="referral-run-btn">
            <Play className="h-4 w-4 mr-2" />
            {running ? 'Läuft...' : 'Belohnungen jetzt prüfen'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
